"use client";

import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { ShopMarketplaceListQueryDTO } from "@/modules/merchant/domain/contracts";
import {
  FITMENT_STATUS_LABELS,
  marketplaceReasonLabel,
} from "@/modules/merchant/domain/contracts/marketplace-policy";
import {
  canUseMerchantPermission,
  type ShopMarketplaceListRow,
} from "@/modules/merchant/presentation/api/client";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Badge,
  Button,
  DataTable,
  Input,
  Label,
  Switch,
  toast,
  type TanstackReactTable,
} from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Loader2, RefreshCw, ShoppingBag, Store } from "lucide-react";
import { useMemo, useState } from "react";
import {
  useShopMarketplaceListQuery,
  useShopMarketplaceSettingsQuery,
  useSyncMarketplaceMutation,
  useUpdateShopMarketplaceSettingsMutation,
} from "../api/queries";

const ROUTE = "/app/shops/$shopId/marketplace" as const;

function fitmentBadge(status: ShopMarketplaceListRow["fitmentStatus"]) {
  if (status === "complete") {
    return (
      <Badge className="bg-emerald-600 hover:bg-emerald-600">
        {FITMENT_STATUS_LABELS.complete}
      </Badge>
    );
  }
  return <Badge variant="secondary">{FITMENT_STATUS_LABELS.pending}</Badge>;
}

function onlineStatusLabel(
  row: ShopMarketplaceListRow,
  autoPublish: boolean,
): {
  label: string;
  variant: "default" | "secondary" | "outline" | "destructive";
} {
  if (row.published) {
    return { label: "ເຜີຍແຜ່ແລ້ວ", variant: "default" };
  }
  if (row.ineligibleReasons.includes("LISTING_REMOVED")) {
    return { label: "Admin ເຊື່ອງ/ລຶບ", variant: "destructive" };
  }
  if (row.ineligibleReasons.includes("FITMENT_PENDING")) {
    return { label: "ລໍ fitment", variant: "secondary" };
  }
  if (row.ineligibleReasons.includes("NO_STOCK")) {
    return { label: "ລໍສະຕ໋ອກ", variant: "secondary" };
  }
  if (!autoPublish) {
    return { label: "ປິດ auto-publish", variant: "outline" };
  }
  if (row.eligible) {
    return { label: "ພ້ອມ — ລໍ sync", variant: "outline" };
  }
  return { label: "ຍັງບໍ່ພ້ອມ", variant: "outline" };
}

export function ShopMarketplacePage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: ROUTE });
  const search = (useSearch({ from: ROUTE }) ?? {}) as {
    offset?: number;
    limit?: number;
    q?: string;
  };

  const offset = search.offset ?? 0;
  const limit = search.limit ?? 20;
  const [qDraft, setQDraft] = useState(search.q ?? "");

  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;
  const canPublish = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.marketplace.publish,
    verifiedLevel,
  );

  const settingsQ = useShopMarketplaceSettingsQuery(shopId);
  const autoPublish = settingsQ.data?.autoPublish ?? false;

  const listParams: ShopMarketplaceListQueryDTO = useMemo(
    () => ({
      limit,
      offset,
      q: search.q,
    }),
    [limit, offset, search.q],
  );

  const listQ = useShopMarketplaceListQuery(shopId, listParams);
  const settingsM = useUpdateShopMarketplaceSettingsMutation(shopId);
  const syncM = useSyncMarketplaceMutation(shopId);

  const rows = listQ.data?.data ?? [];
  const total = listQ.data?.meta.total ?? 0;

  const columns: TanstackReactTable.ColumnDef<ShopMarketplaceListRow>[] =
    useMemo(
      () => [
        {
          id: "product",
          header: "ສິນຄ້າ",
          cell: ({ row }) => (
            <div>
              <p className="font-medium">{row.original.productName}</p>
              <p className="text-muted-foreground text-xs">
                SKU: {row.original.shopSku}
              </p>
            </div>
          ),
        },
        {
          id: "price",
          header: () => <span className="block text-right">ລາຄາ</span>,
          cell: ({ row }) => (
            <span className="block text-right tabular-nums">
              {row.original.price.toLocaleString()} {row.original.currency}
            </span>
          ),
        },
        {
          id: "fitment",
          header: "Fitment",
          cell: ({ row }) => fitmentBadge(row.original.fitmentStatus),
        },
        {
          id: "stock",
          header: () => <span className="block text-right">ສະຕ໋ອກ</span>,
          cell: ({ row }) => (
            <span className="block text-right tabular-nums">
              {row.original.availableQty}
            </span>
          ),
        },
        {
          id: "status",
          header: "Online",
          cell: ({ row }) => {
            const s = onlineStatusLabel(row.original, autoPublish);
            return <Badge variant={s.variant}>{s.label}</Badge>;
          },
        },
        {
          id: "note",
          header: "",
          cell: ({ row }) => {
            if (row.original.published) return null;
            const reasonText =
              row.original.ineligibleReasons.length > 0
                ? row.original.ineligibleReasons
                  .map((code) => marketplaceReasonLabel(code))
                  .join(" · ")
                : null;
            return reasonText ? (
              <span className="max-w-[200px] text-right text-muted-foreground text-xs">
                {reasonText}
              </span>
            ) : null;
          },
        },
      ],
      [autoPublish],
    );

  return (
    <RequireMerchantPermission
      permission={MerchantPermissions.marketplace.read}
    >
      <Header>
        <Breadcrumb items={shopBreadcrumbs.marketplace(shopId)} />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="inline-flex items-center gap-2 font-semibold text-xl">
              <Store className="size-5 text-primary" aria-hidden />
              Marketplace
            </h1>
            <p className="text-muted-foreground text-sm">
              ເປີດ auto-publish — ລະບົບເຜີຍແຜ່ເມື່ອ fitment ຄົບ ແລະ ມີສະຕ໋ອກ
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() =>
                nav({
                  to: "/app/shops/$shopId/marketplace/orders",
                  params: { shopId: shopId! },
                })
              }
            >
              <ShoppingBag className="mr-1 size-4" />
              ຄຳສັ່ງຊື້
            </Button>
            {canPublish ? (
              <Button
                variant="outline"
                disabled={syncM.isPending || !autoPublish}
                onClick={() => {
                  syncM.mutate(undefined, {
                    onSuccess: (res) =>
                      toast.success(
                        `sync: ${res.published} ເຜີຍແຜ່, ${res.skipped} ຂ້າມ`,
                      ),
                    onError: () => toast.error("sync ບໍ່ສຳເລັດ"),
                  });
                }}
              >
                {syncM.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-1 size-4" />
                )}
                Sync ດຽວນີ້
              </Button>
            ) : null}
          </div>
        </div>

        {canPublish ? (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card p-4">
            <div className="space-y-1">
              <Label htmlFor="auto-publish" className="font-medium">
                ຂາຍ online ອັດຕະໂນມັດ
              </Label>
              <p className="text-muted-foreground text-xs">
                ເມື່ອ fitment ຄົບ + ມີສະຕ໋ອກ → listing ຂຶ້ນ Marketplace ໂດຍອັດຕະໂນມັດ
              </p>
            </div>
            <Switch
              id="auto-publish"
              checked={autoPublish}
              disabled={settingsM.isPending || settingsQ.isLoading}
              onCheckedChange={(checked) => {
                settingsM.mutate(
                  { autoPublish: checked },
                  {
                    onSuccess: (res) => {
                      const bulk = res.bulk;
                      toast.success(
                        checked
                          ? bulk
                            ? `ເປີດ auto-publish — ເຜີຍແຜ່ ${bulk.published} ລາຍການ`
                            : "ເປີດ auto-publish ແລ້ວ"
                          : "ປິດ auto-publish ແລ້ວ",
                      );
                    },
                    onError: () => toast.error("ບັນທຶກການຕັ້ງຄ່າບໍ່ສຳເລັດ"),
                  },
                );
              }}
            />
          </div>
        ) : null}

        <form
          className="mb-4 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            nav({
              search: (prev) => ({
                ...prev,
                q: qDraft.trim() || undefined,
                offset: 0,
              }),
            });
          }}
        >
          <Input
            value={qDraft}
            onChange={(e) => setQDraft(e.target.value)}
            placeholder="ຄົ້ນຫາຊື່ / SKU / barcode"
            className="w-56"
          />
          <Button type="submit" variant="secondary">
            ຄົ້ນຫາ
          </Button>
        </form>

        <DataTable
          columns={columns}
          data={rows}
          isLoading={listQ.isLoading}
          pagination={{
            pageIndex: Math.floor(offset / limit),
            pageSize: limit,
            total,
            onPageChange: (page) => {
              nav({
                search: (prev) => ({
                  ...prev,
                  offset: page * limit,
                  limit,
                }),
              });
            },
          }}
        />
      </Main>
    </RequireMerchantPermission>
  );
}
